import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Feed from './Feed';
import Footer from './Footer';
import Messages from './Messages';
import StatusBar from './StatusBar';
import PostDetails from './PostDetails';
import SplashScreen from './SplashScreen';
import { useTheme } from '../App';

function Home() {
  const { theme } = useTheme();
  const [token, setToken] = useState(localStorage.getItem('reddit_access_token'));
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [subreddit, setSubreddit] = useState('hot');
  const [page, setPage] = useState(1);
  const [commandHistory, setCommandHistory] = useState(() => {
    const saved = localStorage.getItem('readdit_command_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [messages, setMessages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [expandedPost, setExpandedPost] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Tabs state to allow quick switching between multiple subreddits and posts. Each tab records
  // whether it represents a subreddit feed or an individual post. When a new subreddit is
  // navigated to or a post is expanded, a new tab will automatically be added. Clicking a tab
  // will restore that view by updating the subreddit or expandedPost state as appropriate.
  // The initial tab corresponds to the default subreddit (e.g. "hot").
  const [tabs, setTabs] = useState([
    { type: 'subreddit', subreddit, label: `r/${subreddit}` },
  ]);
  // Index of the currently active tab. Changing this triggers switching the view.
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const postRefs = useRef([]);
  const afterRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('readdit_command_history', JSON.stringify(commandHistory));
  }, [commandHistory]);

  // When the subreddit changes (via commands or file explorer), ensure that a tab exists for
  // it. If the tab already exists, just activate it. Otherwise create a new tab. This effect
  // runs whenever the subreddit state updates. It helps keep the tabs list in sync with the
  // user's navigation.
  useEffect(() => {
    setTabs(prevTabs => {
      // Look for an existing subreddit tab
      const existingIndex = prevTabs.findIndex(t => t.type === 'subreddit' && t.subreddit === subreddit);
      if (existingIndex !== -1) {
        // Activate the existing tab if it's not already active
        setActiveTabIndex(existingIndex);
        return prevTabs;
      }
      // Create a new subreddit tab with an appropriate label
      const newTab = { type: 'subreddit', subreddit: subreddit, label: `r/${subreddit}` };
      const newTabs = [...prevTabs, newTab];
      setActiveTabIndex(newTabs.length - 1);
      return newTabs;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subreddit]);

  // When the active tab index changes, update the main view. For subreddit tabs, change the
  // subreddit state and clear any expanded post. For post tabs, simply set the expanded post
  // to the stored post data so that the details panel shows the post. This effect ensures
  // that switching tabs triggers the correct view without refetching unnecessarily.
  useEffect(() => {
    const tab = tabs[activeTabIndex];
    if (!tab) return;
    if (tab.type === 'subreddit') {
      // Avoid setting the subreddit again if it's already the same to prevent loops.
      if (tab.subreddit !== subreddit) {
        setSubreddit(tab.subreddit);
      }
      // Close any expanded post when switching back to a subreddit feed
      if (expandedPost) {
        setExpandedPost(null);
      }
    } else if (tab.type === 'post') {
      // Ensure the expanded post matches the tab's post
      const tabPost = tab.post;
      if (!expandedPost || expandedPost.id !== tabPost.id) {
        setExpandedPost(tabPost);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex]);

  useEffect(() => {
    if (selectedIndex >= 0 && postRefs.current[selectedIndex] && !expandedPost) {
      postRefs.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedIndex, expandedPost]);

  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const findCommandSuggestion = (cmd) => {
    const commands = ['next', 'back', 'refresh', 'home', 'popular', 'news', 'help', 'clear', 'ls', 'dir', 'cd'];
    const closest = commands.find(c => c.startsWith(cmd.substring(0, 2)));
    return closest;
  };

  const fetchFeed = useCallback(async () => {
    if (!token) {
      console.log('No token available, skipping fetch');
      return;
    }
    
    setLoading(true);
    setSelectedIndex(-1);
    setExpandedPost(null);

    const limit = 35;
    const isGenericFeed = ['hot', 'popular'].includes(subreddit);
    const basePath = isGenericFeed ? subreddit : `r/${subreddit}`;
    let url = `https://oauth.reddit.com/${basePath}.json?limit=${limit}`;

    if (page > 1 && afterRef.current) {
      url += `&after=${afterRef.current}`;
    }

    console.log('Fetching:', url);

    try {
      if (!username) {
        const userRes = await fetch('https://oauth.reddit.com/api/v1/me', { headers: { 'Authorization': `Bearer ${token}` } });
        const userData = await userRes.json();
        setUsername(userData.name);
      }
      const feedRes = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (feedRes.status === 401) {
        localStorage.removeItem('reddit_access_token');
        navigate('/login');
        return;
      }
      const feedData = await feedRes.json();
      postRefs.current = postRefs.current.slice(0, feedData.data.children.length);
      setFeed(feedData.data.children);
      afterRef.current = feedData.data.after;
    } catch (error) {
      console.error('Fetch error:', error);
      addMessage({ type: 'output', content: `Error loading ${subreddit}: ${error.message}` });
    }
    setLoading(false);
    setInitialLoading(false);
  }, [token, username, subreddit, page, navigate, addMessage]);

  const handleCommand = useCallback((command) => {
    const fullCmd = command.trim();
    const parts = fullCmd.split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    const resetPage = () => {
      setPage(1);
      afterRef.current = null;
    };

    if (cmd === 'get-post' && args.length > 0) {
      const topMatch = args.join(' ').match(/-top\s+(\d+)/i);
      if (topMatch) {
        const count = parseInt(topMatch[1]);
        const topPosts = [...feed]
          .sort((a, b) => b.data.score - a.data.score)
          .slice(0, count);
        addMessage({
          type: 'list',
          posts: topPosts
        });
        return;
      }
    }

    switch (cmd) {
      case 'next':
      case 'n':
        if (afterRef.current) setPage(p => p + 1);
        else addMessage({ type: 'output', content: 'No more posts available...' });
        break;
      case 'back':
      case 'b':
        if (page > 1) {
          setPage(p => p - 1);
          if (page === 2) {
            afterRef.current = null;
          }
        } else {
          addMessage({ type: 'output', content: 'Already at the first page.' });
        }
        break;
      case 'refresh':
      case 'r':
        fetchFeed();
        break;
      case 'news':
        resetPage();
        setSubreddit('news');
        break;
      case 'popular':
      case 'p':
        resetPage();
        setSubreddit('popular');
        break;
      case 'home':
      case 'h':
        resetPage();
        setSubreddit('hot');
        break;
      case 'help':
      case '?':
        addMessage({
          type: 'help',
          commands: [
            { command: 'next (n)', description: 'Go to next page' },
            { command: 'back (b)', description: 'Go to previous page' },
            { command: 'refresh (r)', description: 'Refresh current feed' },
            { command: 'home (h)', description: 'Go to home feed' },
            { command: 'popular (p)', description: 'Go to r/popular' },
            { command: 'news', description: 'Go to r/news' },
            { command: 'cd r/<name>', description: 'Navigate to any subreddit' },
            { command: 'ls / dir', description: 'List current posts in table format' },
            { command: 'clear', description: 'Clear terminal output' },
            { command: 'clear-history', description: 'Clear command history' },
            { command: 'Get-Post -Top <n>', description: 'Show top N posts by score' },
            { command: 'help (?)', description: 'Show this help message' },
          ]
        });
        break;
      case 'clear-history':
        setCommandHistory([]);
        localStorage.removeItem('readdit_command_history');
        addMessage({
          type: 'output',
          content: 'Command history cleared successfully.'
        });
        break;
      case 'clear':
      case 'cls':
        setMessages([]);
        break;
      case 'ls':
      case 'dir':
        addMessage({
          type: 'list',
          posts: feed
        });
        break;
      case 'cd':
        if (args.length > 0) {
          const target = args.join(' '); // Join all args to handle spaces
          const subMatch = target.match(/^r\/(.+)$/);
          if (subMatch) {
            resetPage();
            setSubreddit(subMatch[1]);
            addMessage({ type: 'output', content: `Navigating to r/${subMatch[1]}...` });
          } else {
            addMessage({ type: 'output', content: `Usage: cd r/subredditname` });
          }
        } else {
          addMessage({ type: 'output', content: 'cd: missing operand\nUsage: cd r/subredditname' });
        }
        break;
      default:
        const isNotJustNumbers = !/^\d+$/.test(cmd);
        if (cmd.match(/^[a-zA-Z0-9_]+$/) && isNotJustNumbers) {
          resetPage();
          setSubreddit(cmd);
          addMessage({ type: 'output', content: `Loading r/${cmd}...` });
        } else {
          const suggestion = findCommandSuggestion(cmd);
          addMessage({ type: 'error', command: fullCmd, suggestion: suggestion });
        }
    }
  }, [page, feed, fetchFeed, addMessage]);

  // Listen for file system commands - moved after handleCommand definition
  useEffect(() => {
    const handleFileSystemCommand = (e) => {
      // Only handle commands if we're authenticated
      if (token) {
        handleCommand(e.detail);
      }
    };
    
    window.addEventListener('fileSystemCommand', handleFileSystemCommand);
    return () => window.removeEventListener('fileSystemCommand', handleFileSystemCommand);
  }, [handleCommand, token]);

  const selectPost = async (postData) => {
    setDetailsLoading(true);
    setExpandedPost(postData);
    
    const accessToken = localStorage.getItem('reddit_access_token');
    try {
      const res = await fetch(`https://oauth.reddit.com${postData.permalink}.json`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = await res.json();
      const fullPost = json[0].data.children[0].data;
      const comments = json[1]?.data?.children || [];
      
      setExpandedPost({
        ...fullPost,
        comments: comments
      });

      // Open a new tab for this post. Use a shortened title for the tab label. Tabs allow
      // users to return to previously opened posts quickly.
      const title = fullPost.title || '';
      const trimmed = title.length > 30 ? `${title.slice(0, 30)}…` : title;
      setTabs(prevTabs => {
        const newTabs = [...prevTabs, { type: 'post', post: { ...fullPost, comments }, label: trimmed }];
        // Make the newly opened post tab active. This ensures the user sees it immediately.
        setActiveTabIndex(newTabs.length - 1);
        return newTabs;
      });

    } catch (error) {
      console.error("Failed to fetch post details:", error);
    }
    setDetailsLoading(false);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT') return;
      switch (e.key) {
        case 'j':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, feed.length - 1));
          break;
        case 'k':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'o':
          e.preventDefault();
          if (selectedIndex >= 0 && feed[selectedIndex]) {
            const post = feed[selectedIndex].data;
            selectPost(post);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setExpandedPost(null);
          break;
        case '/':
          e.preventDefault();
          document.querySelector('input')?.focus();
          break;
        case '?':
          if (e.target.tagName !== 'INPUT') {
            e.preventDefault();
            handleCommand('help');
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [feed, selectedIndex, handleCommand]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    if (accessToken) {
      localStorage.setItem('reddit_access_token', accessToken);
      setToken(accessToken);
      navigate('/', { replace: true });
    } else if (!token) {
      navigate('/login');
    }
  }, [location, navigate, token]);

  useEffect(() => {
    if (token) {
      fetchFeed();
    }
  }, [subreddit, page, token, fetchFeed]);

  const handleLogout = () => {
    localStorage.removeItem('reddit_access_token');
    setToken(null);
    navigate('/login');
  };

  // If no token yet, don't early return here. Instead, allow useEffect below to
  // redirect unauthenticated users to the login page. We still display the
  // splash screen while the app determines the authentication state. Without
  // this, the component would return null immediately and the redirect would
  // never occur.
  if (initialLoading || !token) return <SplashScreen />;

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const mainStyle = {
    display: 'flex',
    gap: '20px',
    flex: 1,
    overflow: 'hidden',
  };

  const feedContainerStyle = {
    flex: expandedPost ? '0 0 45%' : '1',
    transition: 'flex 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const detailsContainerStyle = {
    flex: '1',
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    boxShadow: `0 0 15px ${theme.colors.shadow}`,
    display: expandedPost ? 'block' : 'none',
    animation: expandedPost ? 'slideIn 0.3s ease-out' : 'none',
    overflow: 'hidden',
  };

  return (
    <div style={containerStyle}>
      <Header username={username} onLogout={handleLogout} />

      {/* Tab bar: displays open subreddits and posts for quick navigation. */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${theme.colors.border}`, backgroundColor: theme.colors.background2, overflowX: 'auto' }}>
        {tabs.map((tab, index) => (
          <div
            key={index}
            onClick={() => setActiveTabIndex(index)}
            style={{
              padding: '5px 10px',
              cursor: 'pointer',
              backgroundColor: index === activeTabIndex ? theme.colors.background3 : 'transparent',
              color: theme.colors.foreground,
              borderRight: `1px solid ${theme.colors.border}`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '220px'
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <div style={mainStyle}>
        <div style={feedContainerStyle}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <Messages messages={messages} />
            <Feed
              posts={feed}
              loading={loading}
              selectedIndex={selectedIndex}
              postRefs={postRefs}
              onSelectPost={selectPost}
            />
          </div>
          <Footer
            onCommand={handleCommand}
            commandHistory={commandHistory}
            setCommandHistory={setCommandHistory}
          />
        </div>
        
        {expandedPost && (
          <div style={detailsContainerStyle}>
            <PostDetails
              post={expandedPost}
              loading={detailsLoading}
              onClose={() => setExpandedPost(null)}
            />
          </div>
        )}
      </div>
      <StatusBar
        subreddit={subreddit}
        page={page}
        totalPosts={feed.length}
        loading={loading}
        selectedIndex={selectedIndex}
      />
    </div>
  );
}

export default Home;